import { useMemo, useState } from 'react';
import {
  TrashIcon,
  FileIcon,
  CopyIcon,
  CheckIcon,
  LayoutGridIcon,
  ListIcon,
  SearchIcon,
  PlayIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { Asset } from '@/types/entities';
import { BASE_URL } from '@/config/constants';

const imageExtensions = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'avif', 'svg'];
const videoExtensions = ['mp4', 'webm', 'ogg', 'mov'];

type AssetsGridProps = {
  assets: Asset[];
  onDelete: (asset: Asset) => void;
  tenantSlug: string;
  isDeleting?: boolean;
}

type PreviewAsset = {
  asset: Asset;
  url: string;
  isImage: boolean;
  isVideo: boolean;
  extension: string;
};

type ViewMode = 'grid' | 'list';

export function AssetsGrid({ assets, onDelete, tenantSlug, isDeleting }: AssetsGridProps) {
  const [previewAsset, setPreviewAsset] = useState<PreviewAsset | null>(null);
  const [copiedAssetId, setCopiedAssetId] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>('grid');

  const assetUrls = useMemo(() => {
    return assets.map(asset => {
      const extension = asset.filepath.split('.').pop()?.toLowerCase() || '';
      const isImage = imageExtensions.includes(extension);
      const isVideo = videoExtensions.includes(extension);
      return {
        asset,
        url: `${BASE_URL}/tenants/${tenantSlug}/assets/${asset.id}/content`,
        isImage,
        isVideo,
        extension,
      };
    });
  }, [assets, tenantSlug]);

  const filteredAssetUrls = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) {
      return assetUrls;
    }

    return assetUrls.filter(({ asset }) => {
      return asset.name.toLowerCase().includes(query) || asset.filepath.toLowerCase().includes(query);
    });
  }, [assetUrls, searchTerm]);

  const handleCopyPath = async (asset: Asset) => {
    try {
      await navigator.clipboard.writeText(asset.filepath);
      setCopiedAssetId(asset.id);
      setTimeout(() => {
        setCopiedAssetId((currentId) => (currentId === asset.id ? null : currentId));
      }, 1500);
    } catch {
      setCopiedAssetId(null);
    }
  };

  const renderAssetMedia = (isImage: boolean, isVideo: boolean, url: string, name: string, extension: string) => {
    if (isImage) {
      return (
        <img
          src={url}
          alt={name}
          className="h-full w-full object-cover transition duration-300 group-hover:scale-[1.03]"
        />
      );
    }

    if (isVideo) {
      return (
        <div className="relative h-full w-full">
          <video src={url} className="h-full w-full object-cover" />
          <div className="absolute inset-0 flex items-center justify-center bg-black/15">
            <span className="flex size-9 items-center justify-center rounded-full bg-white/80 text-foreground shadow-sm">
              <PlayIcon className="size-4 fill-current" />
            </span>
          </div>
        </div>
      );
    }

    return (
      <div className="flex h-full w-full flex-col items-center justify-center gap-2 bg-muted/50 text-muted-foreground">
        <FileIcon className="size-6" />
        <span className="rounded bg-background/80 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide">
          {extension || 'file'}
        </span>
      </div>
    );
  };

  return (
    <>
      <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="relative w-full md:max-w-lg">
          <SearchIcon className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Search by filename or filepath"
            className="h-10 border-muted bg-muted/20 pl-9"
            aria-label="Search assets"
          />
        </div>

        <div className="flex items-center gap-2 self-end md:self-auto">
          <div className="inline-flex items-center rounded-md border bg-muted/20 p-0.5">
            <Button
              type="button"
              variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
              size="sm"
              className="h-8 gap-1.5 px-2.5"
              onClick={() => setViewMode('grid')}
            >
              <LayoutGridIcon className="size-3.5" />
              Grid
            </Button>
            <Button
              type="button"
              variant={viewMode === 'list' ? 'secondary' : 'ghost'}
              size="sm"
              className="h-8 gap-1.5 px-2.5"
              onClick={() => setViewMode('list')}
            >
              <ListIcon className="size-3.5" />
              List
            </Button>
          </div>
        </div>
      </div>

      {filteredAssetUrls.length === 0 ? (
        <div className="rounded-xl border border-dashed border-muted-foreground/30 bg-muted/10 p-12 text-center text-sm text-muted-foreground">
          {assets.length === 0 ? 'No assets uploaded yet.' : 'No assets match your search.'}
        </div>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-4">
          {filteredAssetUrls.map(({ asset, url, isImage, isVideo, extension }) => {
            return (
              <div key={asset.id} className="group relative">
                <button
                  type="button"
                  onClick={() => setPreviewAsset({ asset, url, isImage, isVideo, extension })}
                  className="relative aspect-[4/3] w-full overflow-hidden rounded-lg border border-border/70 bg-card text-left shadow-sm transition hover:border-border focus-visible:outline-hidden"
                >
                  {renderAssetMedia(isImage, isVideo, url, asset.name, extension)}

                  <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 via-black/40 to-transparent px-3 pb-2 pt-7 text-white">
                    <p className="truncate text-xs font-medium">{asset.name}</p>
                    <p className="truncate text-[10px] opacity-80">{asset.filepath}</p>
                  </div>
                </button>

                <div className="absolute right-2 top-2 flex items-center gap-1 opacity-0 transition group-hover:opacity-100 group-focus-within:opacity-100">
                  <Button
                    variant="secondary"
                    size="icon"
                    onClick={(event) => {
                      event.stopPropagation();
                      void handleCopyPath(asset);
                    }}
                    className="h-7 w-7 bg-background/90"
                    aria-label="Copy filepath"
                  >
                    {copiedAssetId === asset.id ? (
                      <CheckIcon className="size-3.5 text-emerald-600" />
                    ) : (
                      <CopyIcon className="size-3.5" />
                    )}
                  </Button>
                  <Button
                    variant="secondary"
                    size="icon"
                    onClick={(event) => {
                      event.stopPropagation();
                      onDelete(asset);
                    }}
                    disabled={isDeleting}
                    className="h-7 w-7 bg-background/90 text-destructive hover:text-destructive"
                    aria-label="Delete asset"
                  >
                    <TrashIcon className="size-3.5" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="space-y-2">
          {filteredAssetUrls.map(({ asset, url, isImage, isVideo, extension }) => {
            return (
              <div key={asset.id} className="flex items-center gap-3 rounded-lg border bg-card/60 p-2">
                <button
                  type="button"
                  onClick={() => setPreviewAsset({ asset, url, isImage, isVideo, extension })}
                  className="h-14 w-20 shrink-0 overflow-hidden rounded border bg-muted/30 focus-visible:outline-hidden"
                >
                  {renderAssetMedia(isImage, isVideo, url, asset.name, extension)}
                </button>

                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{asset.name}</p>
                  <p className="truncate font-mono text-xs text-muted-foreground">{asset.filepath}</p>
                </div>

                <div className="flex shrink-0 items-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => void handleCopyPath(asset)}
                    className="h-8 w-8"
                    aria-label="Copy filepath"
                  >
                    {copiedAssetId === asset.id ? (
                      <CheckIcon className="size-4 text-emerald-600" />
                    ) : (
                      <CopyIcon className="size-4" />
                    )}
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onDelete(asset)}
                    disabled={isDeleting}
                    className="h-8 w-8 text-destructive hover:text-destructive"
                    aria-label="Delete asset"
                  >
                    <TrashIcon className="size-4" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={!!previewAsset} onOpenChange={(open) => !open && setPreviewAsset(null)}>
        {previewAsset && (
          <DialogContent className="max-w-5xl p-0">
            <DialogHeader className="px-6 pt-6">
              <DialogTitle>{previewAsset.asset.name}</DialogTitle>
              <DialogDescription>Asset ID: {previewAsset.asset.id}</DialogDescription>
            </DialogHeader>
            <div className="px-6 pb-6">
              {previewAsset.isImage ? (
                <img
                  src={previewAsset.url}
                  alt={previewAsset.asset.name}
                  className="max-h-[70vh] w-full rounded-md object-contain"
                />
              ) : previewAsset.isVideo ? (
                <video
                  src={previewAsset.url}
                  controls
                  className="max-h-[70vh] w-full rounded-md"
                />
              ) : (
                <div className="flex items-center justify-center rounded-md border bg-muted/30 p-10 text-muted-foreground">
                  Preview not available for this file type ({previewAsset.extension || 'unknown'}).
                </div>
              )}
            </div>
          </DialogContent>
        )}
      </Dialog>
    </>
  );
}
